import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { StickyNote, Trash2, BookOpen, Crown } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import { toast } from "sonner";

const BIBLE_BOOKS = {
  genese: "Genèse", exode: "Exode", levitique: "Lévitique", nombres: "Nombres",
  deuteronome: "Deutéronome", josue: "Josué", juges: "Juges", ruth: "Ruth",
  "1samuel": "1 Samuel", "2samuel": "2 Samuel", "1rois": "1 Rois", "2rois": "2 Rois",
  "1chroniques": "1 Chroniques", "2chroniques": "2 Chroniques", esdras: "Esdras",
  nehemie: "Néhémie", tobie: "Tobie", judith: "Judith", esther: "Esther",
  "1maccabees": "1 Maccabées", "2maccabees": "2 Maccabées", job: "Job",
  psaumes: "Psaumes", proverbes: "Proverbes", ecclesiaste: "Ecclésiaste",
  cantique: "Cantique", sagesse: "Sagesse", siracide: "Siracide", isaie: "Isaïe",
  jeremie: "Jérémie", lamentations: "Lamentations", baruch: "Baruch",
  ezechiel: "Ézéchiel", daniel: "Daniel", osee: "Osée", joel: "Joël", amos: "Amos",
  abdias: "Abdias", jonas: "Jonas", michee: "Michée", nahum: "Nahum",
  habacuc: "Habacuc", sophonie: "Sophonie", aggee: "Aggée", zacharie: "Zacharie",
  malachie: "Malachie", matthieu: "Matthieu", marc: "Marc", luc: "Luc", jean: "Jean",
  actes: "Actes", romains: "Romains", "1corinthiens": "1 Corinthiens",
  "2corinthiens": "2 Corinthiens", galates: "Galates", ephesiens: "Éphésiens",
  philippiens: "Philippiens", colossiens: "Colossiens",
  "1thessaloniciens": "1 Thessaloniciens", "2thessaloniciens": "2 Thessaloniciens",
  "1timothee": "1 Timothée", "2timothee": "2 Timothée", tite: "Tite",
  philemon: "Philémon", hebreux: "Hébreux", jacques: "Jacques",
  "1pierre": "1 Pierre", "2pierre": "2 Pierre", "1jean": "1 Jean",
  "2jean": "2 Jean", "3jean": "3 Jean", jude: "Jude", apocalypse: "Apocalypse"
};

const NotesPage = () => {
  const navigate = useNavigate();
  const { user, isPremium } = useAuth();
  const [notes, setNotes] = useState(() => {
    const saved = localStorage.getItem("bible-notes");
    return saved ? JSON.parse(saved) : {};
  });

  const parseKey = (key) => {
    const parts = key.split("-");
    const verse = parts.pop();
    const chapter = parts.pop();
    const bookId = parts.join("-");
    return { bookId, chapter, verse };
  };

  const deleteNote = (key) => {
    setNotes(prev => {
      const newNotes = { ...prev };
      delete newNotes[key];
      localStorage.setItem("bible-notes", JSON.stringify(newNotes));
      return newNotes;
    });
    toast.success("Note supprimée");
  };

  const notesList = Object.entries(notes).map(([key, note]) => {
    const { bookId, chapter, verse } = parseKey(key);
    return {
      key,
      bookId,
      bookName: BIBLE_BOOKS[bookId] || bookId,
      chapter,
      verse,
      text: note.text,
      createdAt: note.createdAt
    };
  }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));

  if (!isPremium) {
    return (
      <div className="container mx-auto max-w-2xl px-4 py-12 text-center">
        <StickyNote className="h-16 w-16 mx-auto text-ink-secondary mb-4" />
        <h1 className="font-heading text-2xl text-ink-primary mb-4">Mes notes</h1>
        <p className="text-ink-secondary mb-6">Les notes sont réservées aux membres Premium</p>
        <Button onClick={() => navigate("/premium")} className="bg-gold-leaf hover:bg-gold-leaf/90">
          <Crown className="mr-2 h-4 w-4" /> Passer Premium
        </Button>
      </div>
    );
  }

  return (
    <div className="container mx-auto max-w-3xl px-4 py-8 animate-fade-in">
      <h1 className="font-heading text-3xl text-ink-primary mb-8 text-center flex items-center justify-center gap-3">
        <StickyNote className="h-8 w-8 text-gold-leaf" />
        Mes notes
      </h1>

      {notesList.length === 0 ? (
        <div className="text-center py-12">
          <StickyNote className="h-16 w-16 mx-auto text-ink-secondary mb-4 opacity-50" />
          <p className="text-ink-secondary font-body mb-4">Vous n'avez pas encore de notes</p>
          <Button asChild variant="outline">
            <Link to="/bible/genese/1">
              <BookOpen className="mr-2 h-4 w-4" /> Commencer à lire
            </Link>
          </Button>
        </div>
      ) : (
        <div className="space-y-4">
          {notesList.map((note) => (
            <Card key={note.key} className="border-border hover:border-gold-leaf transition-colors">
              <CardContent className="p-4">
                <div className="flex justify-between items-start gap-4">
                  <Link 
                    to={`/bible/${note.bookId}/${note.chapter}`}
                    className="flex-1 group"
                  >
                    <p className="font-heading text-lg text-ink-primary group-hover:text-rubric-red transition-colors">
                      {note.bookName} {note.chapter}:{note.verse}
                    </p>
                    <p className="font-body text-ink-secondary mt-2 italic">
                      {note.text}
                    </p>
                    <p className="text-xs text-ink-secondary mt-2">
                      {new Date(note.createdAt).toLocaleDateString('fr-FR', {
                        day: 'numeric', month: 'long', year: 'numeric'
                      })}
                    </p>
                  </Link>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteNote(note.key)}
                    className="text-ink-secondary hover:text-rubric-red"
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default NotesPage;
